import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UserSearchResult } from '@promo/shared';
import * as api from '@/lib/api';

export type { UserSearchResult };

function useDebouncedValue(value: string, delayMs: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => window.clearTimeout(timeoutId);
  }, [delayMs, value]);

  return debouncedValue;
}

export function useUserSearch(query: string, limit = 10) {
  const trimmedQuery = query.trim();
  const debouncedQuery = useDebouncedValue(trimmedQuery, 250);

  const result = useQuery<UserSearchResult[], Error>({
    queryKey: ['users', 'search', debouncedQuery, limit],
    queryFn: () =>
      api.get<UserSearchResult[]>('/users/search', {
        q: debouncedQuery,
        limit: String(limit),
      }),
    enabled: debouncedQuery.length >= 2,
    staleTime: 60_000,
  });

  return {
    ...result,
    debouncedQuery,
  };
}
